import { t } from 'i18next';
import { Center, Group, Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { ColumnBadge } from 'components/ColumnBadge';
import { Column } from 'utils/models';
import { formatDate } from 'utils/dates';
import { TableCheckBox } from 'components/Table/TableBody/TableCheckbox';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import { updateGroupVisibility } from 'services/groups';
import { queryKeys } from 'utils/query';
import { useAppContext } from 'context/hooks';
import { DataFieldNames } from 'pages/Settings/GroupSettingsTable.config';
import { GroupSettingsTabs } from 'pages/Settings/Forms/GroupSettings.helpers';
import { GroupEnlistment } from 'models/groups';

export const GroupSettingsColumns = (): Column<GroupEnlistment>[] => {
  const navigate = useNavigate();
  const { mutate: changeVisibility, isPending } = useCustomMutation({
    fn: updateGroupVisibility,
    onSuccess: { invalidateQueryKeys: [[queryKeys.groupSettings], [queryKeys.userData]] },
  });
  const { visibleGroupsIds, currentUser } = useAppContext();

  const getRoles = (item: GroupEnlistment) => {
    // Find ID in property userInfo where staffEnlistments is equal to item ID
    const findRolesById = currentUser?.user.staffEnlistments?.find((role) => role.groupId === item.id);
    // Then map the roles available in the Roles Array
    return findRolesById?.roles?.map((role) => role.name);
  };

  const getMemberSince = (item: GroupEnlistment) => {
    // Find ID in property userInfo where staffEnlistments is equal to item ID
    const findRolesById = currentUser?.user.staffEnlistments?.find((role) => role.groupId === item.id);
    // Then map the timestamps per roles available in the Roles Array
    const timestamp = findRolesById?.roles?.map((role) => role.creationTimestamp);

    return timestamp ? formatDate({ timestamp: Number(timestamp[0]) }) : '';
  };

  const checkboxOnChange = async (item: GroupEnlistment) => {
    changeVisibility({ groupId: item.id, visible: item.hidden, visibleGroupsIds });
  };

  const columns: Column<GroupEnlistment>[] = [
    {
      header: t('name_label'),
      width: 25,
      renderItem: (item) => <b>{item.name}</b>,
      onClick: (item) => navigate(`/settings/${item.id}/${GroupSettingsTabs.General}`),
      sortFieldName: DataFieldNames.name,
    },
    {
      header: t('description_label'),
      width: 25,
      renderItem: (item) => <Text>{item.description}</Text>,
    },
    {
      header: t('group_role_label'),
      width: 25,
      renderItem: (item) => (
        <Group gap={'xs'}>
          {getRoles(item)?.map((role) => (
            <ColumnBadge label={role} key={`role-${role}`} />
          ))}
        </Group>
      ),
    },
    {
      header: t('group_member_since_label'),
      width: 20,
      renderItem: (item) => <Text>{getMemberSince(item)}</Text>,
    },
    {
      header: t('group_visible_label'),
      headerTooltip: t('group_visible_tooltip'),
      width: 5,
      renderItem: (item) => (
        <Center>
          <TableCheckBox
            disabled={isPending}
            onChange={() => checkboxOnChange(item)}
            checked={!item.hidden}
            color={'blue'}
          />
        </Center>
      ),
    },
  ];
  return columns;
};
